import React, { useState } from "react";
import { toast } from "sonner";
import { saveMyPart2Response, downloadPart2Pdf } from "@/lib/api";

const entryNo = (key) => Number(key.split("__")[1] || 1);
export const entryKeys = (forms, id) =>
  [id, ...Object.keys(forms).filter((k) => k.startsWith(`${id}__`))].filter((k, i, a) => a.indexOf(k) === i).sort((a, b) => entryNo(a) - entryNo(b));

export const formToPdf = (form, answers) => ({
  title: form.title, intro: form.intro || "",
  fields: form.fields.map((f) => ({ label: f.label, type: f.type, value: answers ? answers[f.id] ?? "" : "" })),
});

export async function pdfDownload(meta, forms, blank, t) {
  try {
    await downloadPart2Pdf({ ...meta, blank, forms, labels: { participant: t("pdfParticipant"), date: t("pdfDate"), kicker: meta.kicker } },
      `rbl-${blank ? "blank" : "my-answers"}.pdf`);
  } catch { toast.error(t("toastWsErr")); }
}

function ScaleInput({ value, onChange, testid }) {
  return (
    <div style={{ display: "flex", gap: 8 }} data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" className={`rbl-lang-btn ${value === n ? "active" : ""}`} style={{ minWidth: 44, height: 40, border: "1px solid var(--line)" }}
          data-testid={`${testid}-${n}`} onClick={() => onChange(n)}>{n}</button>
      ))}
    </div>
  );
}

export default function Part2Form({ day, form, state, onChange, onSaved, t, pdfMeta }) {
  const answers = state?.answers || {};
  const [busy, setBusy] = useState(false);
  const set = (id, v) => onChange(form.id, { ...answers, [id]: v });

  const save = async () => {
    setBusy(true);
    try {
      await saveMyPart2Response({ day_number: day, form_id: form.id, form_title: form.title, answers,
        labels: Object.fromEntries(form.fields.map((f) => [f.id, f.label])) });
      onSaved(form.id);
      toast.success(t("formSavedToast"));
    } catch { toast.error(t("toastWsErr")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rbl-worksheet" data-testid={`p2form-${form.id}`} style={{ marginTop: 18 }}>
      <h4>{form.title}</h4>
      {form.intro && <div className="hint">{form.intro}</div>}
      {form.fields.map((f) => (
        <div className="rbl-field" key={f.id}>
          <label className="rbl-label">{f.label}</label>
          {f.type === "scale"
            ? <ScaleInput value={answers[f.id]} onChange={(v) => set(f.id, v)} testid={`p2form-${form.id}-${f.id}`} />
            : <textarea className="rbl-input" data-testid={`p2form-${form.id}-${f.id}`} placeholder={t("typeAnswer")}
                value={answers[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} />}
        </div>
      ))}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button className="rbl-btn green" disabled={busy} onClick={save} data-testid={`p2form-${form.id}-save`}>{busy ? "…" : t("formSave")}</button>
        {state?.saved && <span style={{ color: "var(--green)", fontSize: 13 }} data-testid={`p2form-${form.id}-saved`}>✓ {t("formSaved")}</span>}
        {pdfMeta && (
          <span style={{ marginLeft: "auto", display: "flex", gap: 14 }}>
            <button className="rbl-link" style={{ border: "none", background: "none" }} data-testid={`p2form-${form.id}-pdf-blank`}
              onClick={() => pdfDownload(pdfMeta, [formToPdf(form)], true, t)}>⬇ {t("pdfBlank")}</button>
            <button className="rbl-link" style={{ border: "none", background: "none" }} data-testid={`p2form-${form.id}-pdf-filled`}
              onClick={() => pdfDownload(pdfMeta, [formToPdf(form, answers)], false, t)}>⬇ {t("pdfFilled")}</button>
          </span>
        )}
      </div>
    </div>
  );
}

export function Part2FormGroup({ form, forms, setForms, multi, ...rest }) {
  const keys = multi ? entryKeys(forms, form.id) : [form.id];
  const addNew = () => {
    const next = Math.max(...keys.map(entryNo)) + 1;
    setForms((s) => ({ ...s, [`${form.id}__${next}`]: { answers: {}, saved: false } }));
  };
  return (
    <div data-testid={`p2group-${form.id}`}>
      {keys.map((k) => (
        <Part2Form key={k} {...rest} state={forms[k]}
          form={{ ...form, id: k, title: entryNo(k) > 1 ? `${form.title} (${entryNo(k)})` : form.title }} />
      ))}
      {multi && (
        <button className="rbl-link" style={{ border: "none", background: "none", marginTop: 10 }} data-testid={`p2form-${form.id}-new`} onClick={addNew}>
          {rest.t("newForm")}
        </button>
      )}
    </div>
  );
}
